/* eslint-disable semi */
/* eslint-disable quotes */
import { forwardRef, useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  debounce,
  findIndex,
  get,
  every,
  isEqual,
  isEmpty,
  take,
  uniqBy,
  uniq,
  without,
  map,
  compact,
} from 'lodash';
import classNames from 'classnames';
import Humanize from 'humanize-plus';
import SelectionBubble from '../SelectionBubble/SelectionBubble';
import Icon from '../Icon/Icon';
import {
  ClearSelections,
  FilterOption,
  NoMatches,
  Search,
  SelectAll,
} from './components';
import {
  buildOptions,
  countOptions,
  createFilterId,
  filterOptions,
  getInitialFilters,
  isMultiLevel,
} from './utils';

const Filter = forwardRef(({
  className = '',
  filterKey = '',
  filterSearchOptions = undefined,
  mustHaveOneSelected = false,
  hideSelectAll = false,
  isSingleOptionSelect = false,
  name,
  pluralName = null,
  onFiltersChange,
  asyncSearch = undefined,
  asyncSearchDebounce = 500,
  asyncLoadingText = 'Loading...',
  options,
  optionsRenderLimit = -1,
  noMatchesPlaceholder = 'No results found',
  searchPlaceholder = 'Search',
  uniqIdPrefix = '',
  dataTestId = '',
  disableParentSelect = false,
  styles = {},
  position = 'right',
}, ref) => {
  const [optionsState, setOptionsState] = useState(options);
  const [activeFilters, setActiveFilters] = useState(getInitialFilters(options));
  const [selectedOptions, setSelectedOptionsState] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const activeFiltersRef = useRef(activeFilters);
  const selectedOptionsRef = useRef(selectedOptions);
  const [availableOptions, setAvailableOptions] = useState(optionsState);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [totalOptionsCount, setTotalOptionsCount] = useState(countOptions(options));
  const filterWrapperRef = useRef(null);
  const searchFieldRef = useRef(null);

  const setAvailableAndSelectedOptions = (newAvailableOptions = [], newOptions = []) => {
    const canBeSelectedOptions = uniqBy([...selectedOptionsRef.current, ...newOptions], 'value');
    const updatedSelectedOptions = canBeSelectedOptions.filter(opt => !opt.children && activeFiltersRef.current.indexOf(opt.value) >= 0);

    setAvailableOptions((optionsRenderLimit < 0) ? newAvailableOptions : take(newAvailableOptions, optionsRenderLimit));
    setSelectedOptionsState(updatedSelectedOptions);
    setLoading(false);
  };

  const getAvailableOptions = (newOptions = []) => {
    if (isMultiLevel(newOptions)) {
      return buildOptions({ options: newOptions, selected: activeFiltersRef.current });
    }
    return newOptions;
  };

  const asyncFilterOptions = async (searchValue) => {
    try {
      setLoading(true);
      const fetchedOptions = await asyncSearch(searchValue);
      const available = getAvailableOptions(fetchedOptions);
      setAvailableAndSelectedOptions(available, fetchedOptions);
      setOptionsState(fetchedOptions);
    } catch (error) {
      setAvailableAndSelectedOptions([], []);
    }
  };

  const debouncedAsyncFilterOptions = useCallback(debounce(asyncFilterOptions, asyncSearchDebounce), []);

  const localFilterOptions = () => {
    let optionsToFilter = optionsState;
    if (isMultiLevel(optionsToFilter)) {
      optionsToFilter = buildOptions({ options: optionsToFilter, selected: activeFilters });
    }

    let available = optionsToFilter;

    if (!isEmpty(search)) {
      const filteredOptions = filterSearchOptions
        ? filterSearchOptions(optionsState, search)
        : filterOptions(optionsState, search);
      available = getAvailableOptions(filteredOptions);
    }
    setAvailableAndSelectedOptions(available, optionsToFilter);
  };

  const onSearchChange = (value) => {
    if (asyncSearch && search !== value) {
      setSearch(value || '');
      setAvailableOptions([]);
      debouncedAsyncFilterOptions(value);
    } else {
      setSearch(value || '');
      localFilterOptions();
    }
  };

  useEffect(() => {
    const parentsWithChildren = availableOptions.filter((o) => get(o, 'hasChildren', false));
    const children = map(parentsWithChildren, 'children', []).flat();
    const options = availableOptions.map((option) => {
      if (!children.includes(option)) {
        return option;
      };
      return null;
    });
    setParentOptions(compact(options));
  }, [availableOptions]);


  useEffect(() => {
    activeFiltersRef.current = activeFilters;
    selectedOptionsRef.current = selectedOptions;
  }, [activeFilters, selectedOptions]);

  useEffect(() => {
    const setActiveFiltersAndOptionsCount = () => {
      const initialFilters = getInitialFilters(options);
      setActiveFilters(initialFilters);
      setTotalOptionsCount(countOptions(options));
      localFilterOptions();
      setOptionsState(options);
    };
    if (!open && !isEqual(options, optionsState)) {
      setActiveFiltersAndOptionsCount();
    }
  }, [options]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (open) {
        const path = e.path || (e.composedPath && e.composedPath());
        const index = findIndex(path, filterWrapperRef.current);
        if (path && index < 0) {
          setOpen(false);
        }
      }
    };
    document.addEventListener('click', onClickOutside);

    setOptionsState(options);
    localFilterOptions();

    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, [open]);

  useEffect(() => {
    localFilterOptions();
  }, [activeFilters]);

  useEffect(() => () => {
    debouncedAsyncFilterOptions.cancel();
  }, [debouncedAsyncFilterOptions]);

  useEffect(() => {
    onSearchChange(search);
  }, [search]);

  const onButtonClick = (e) => {
    e.preventDefault();
    if (!open) {
      searchFieldRef.current.resetValue();
    }
    setOpen(!open);
  };

  const setSelectedOptions = (optionIds) => {
    const newSelectedOptions = optionIds.map(
      id => options.find(o => o.value === id),
    ).filter(o => o !== undefined);
    setSelectedOptionsState(newSelectedOptions);
    setActiveFilters(optionIds);
  };

  useImperativeHandle(ref, () => (
    {
      props: { filterKey },
      setSelectedOptions(values) {
        setSelectedOptions(values)
      },
    }
  ), []);

  const onOptionChange = (filter, value) => {
    const { value: optValue } = filter;
    const hasChildren = get(filter, 'hasChildren', false);
    const children = get(filter, 'children', []);
    const childValues = map(children, 'value');
    const parent = options.find((option) => option?.children?.includes(filter));

    let newFilters = hasChildren ?
      without(activeFilters, ...[optValue, ...childValues]) :
      without(activeFilters, optValue, parent?.value);
    let newSelected = hasChildren ?
      without(selectedOptions, ...[filter, ...children]) :
      without(selectedOptions, filter);

    if (isSingleOptionSelect && value != null) {
      newFilters = [optValue];
      newSelected = [filter];
    } else if (value) {
      if (hasChildren) {
        newFilters = uniq(newFilters.concat(optValue, childValues));
        newSelected = uniqBy([...selectedOptions, ...children], 'value');
      }
      newFilters = uniq(newFilters.concat(optValue));
      newSelected = uniqBy(newSelected.concat(filter), 'value');

      const allChildrenSelected = every(parent?.children, (child) => newFilters.includes(child.value));
      if (parent && allChildrenSelected) {
        newFilters = newFilters.concat(parent?.value);
      }
    }

    setActiveFilters(newFilters);
    setSelectedOptionsState(newSelected);
    onFiltersChange(newFilters, filterKey, {
      setSelectedOptions,
      filterKey: optValue,
    });
    localFilterOptions();
  };

  const onSelectAll = () => {
    let optionsToSelect = options;

    if (isMultiLevel(optionsToSelect)) {
      optionsToSelect = buildOptions({ options: optionsToSelect, selected: activeFilters });
    }

    const newFilters = map(optionsToSelect, (o) => o.value);
    setActiveFilters(newFilters);
    localFilterOptions();
    onFiltersChange(newFilters, filterKey, setSelectedOptions);
  };

  const onClearAll = () => {
    setActiveFilters([]);
    localFilterOptions();
    onFiltersChange([], filterKey, { setSelectedOptions });
  };

  const onLabelClick = (filter) => {
    if (availableOptions.includes(filter)) {
      const parent = options.find((option) => option?.children?.includes(filter));
      const optionId = parent ? `${uniqIdPrefix}${parent.value}__${filter.value}` : `${uniqIdPrefix}${filter.value}`;
      const option = document.getElementById(optionId)
      option.style.scrollMarginTop = '10px';
      option.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  const filterClass = classNames({
    'max-w-350px min-w-250px relative text-text-blue': true,
  }, className);

  const allOptionsSelected = activeFilters.length >= totalOptionsCount;

  const noMatchesFound = availableOptions.length <= 0;

  const filterButtonClass = classNames({
    'font-regular-font text-sm text-text-blue block h-10 px-2.125 pl-2.5 py-0 relative text-left select-none w-full border border-solid rounded': true,
    'border-action-blue overflow-hidden': open,
    'bg-dark-fill-blue hover:bg-light-border-blue border-medium-border-blue': !open && !isEmpty(selectedOptions),
    'hover:bg-medium-fill-grey bg-white border-light-border-blue': !open && isEmpty(selectedOptions),
  });

  const dropdownClass = classNames({
    'block bg-white absolute bg-gradient-to-t from-white to-light-border-grey rounded border border-solid border-action-blue z-dropdown overflow-hidden mt-2 w-full': open,
    'hidden border-light-border-blue border-t-0 border': !open,
    'right-0': position === 'left',
    'left-0': position === 'right',
  });

  const countClass = classNames(
    { 'inline-block ml-1 font-bold font-heavy-font': true },
    { hidden: selectedOptions.length === 0 },
  );

  const checkIconClass = classNames(
    { 'inline-block mr-2': true },
    { hidden: selectedOptions.length <= 0 },
  );

  const count = selectedOptions.length;

  const accessibilityLabel = `${Humanize.titleCase(Humanize.pluralize(count || 1, name, pluralName))} (${count}) selected, dropdown`;

  return (
    <div
      className={filterClass}
      id={`${createFilterId(name)}-container`}
      ref={filterWrapperRef}
    >
      <button
        role="listbox"
        onClick={onButtonClick}
        className={filterButtonClass}
        aria-label={accessibilityLabel}
        data-testid={dataTestId}
        style={styles.button}
      >

        <span className={checkIconClass}>
          <Icon className="mb-1" color="text-blue" viewBox="0 0 20 20" icon="Check" size={14} />
        </span>

        {Humanize.titleCase(
          Humanize.pluralize(count || 1, name, pluralName),
        )}

        <span className={countClass}>
          (<span className="mx-px font-bold font-heavy-font">{count}</span>)
        </span>
        <Icon
          className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none"
          viewBox="0 0 20 20"
          icon="ChevronArrow"
          direction={open ? "up" : "down"}
          size={16}
          color={'medium-grey'}
        />
      </button>

      <div className={dropdownClass} style={styles.dropdown}>
        <Search
          id={createFilterId(name)}
          label={`Search ${name}`}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          ref={searchFieldRef}
        />

        <div className="overflow-y-auto select-none max-h-450px relative text-text-blue">
          {
            !isEmpty(selectedOptions) ? (
              <div>
                <div className="flex-wrap px-2.5 pt-3">
                  {
                    selectedOptions.map(o => (
                      <SelectionBubble
                        key={`uOpt-${o.value}-selected`}
                        label={o.label}
                        hideClose={mustHaveOneSelected && count === 1}
                        selected={o}
                        onClick={onOptionChange}
                        onLabelClick={onLabelClick}
                        clickable={onLabelClick && availableOptions.includes(o)}
                      />
                    ))
                  }
                </div>

                {mustHaveOneSelected ?
                  <div className={'border-b border-light-border-blue'} /> :
                  <ClearSelections onClick={onClearAll} allOptionsSelected={allOptionsSelected} />
                }
              </div>
            ) : null
          }
          <div>
            {
              loading ? (
                <div className="pt-3 pl-2.5 pb-2.5 text-sm box-content block font-regular-font capitalize text-brand-blue tracking-normal">
                  {asyncLoadingText}
                </div>
              ) : null
            }

            <SelectAll
              hide={hideSelectAll || noMatchesFound || allOptionsSelected || optionsRenderLimit > 0}
              onSelectAll={onSelectAll}
            />

            {
              parentOptions.map(o => (
                <FilterOption
                  activeFilters={activeFilters}
                  key={`uOpt-${o?.value}`}
                  onChange={onOptionChange}
                  option={o}
                  searchTerm={search}
                  uniqIdPrefix={uniqIdPrefix}
                  disableParentSelect={disableParentSelect}
                />
              ))
            }
            <NoMatches
              noMatchesFound={!loading && (allOptionsSelected || noMatchesFound)}
              placeholder={noMatchesPlaceholder}
            />
          </div>
        </div>
      </div>
    </div>
  );
});


Filter.propTypes = {
  /** className for reference */
  className: PropTypes.string,
  /** Filter key used when storing selected filter to state. i.e. serviceTypes */
  filterKey: PropTypes.string,
  /** returns an array of filtered searched ServiceType options */
  filterSearchOptions: PropTypes.func,
  /** Don't allow user to remove all options */
  mustHaveOneSelected: PropTypes.bool,
  /** Should hide select all button */
  hideSelectAll: PropTypes.bool,
  /** Only allows one option to be selected at a time */
  isSingleOptionSelect: PropTypes.bool,
  /** Singular name to display on Filter button (i.e. 'Population') */
  name: PropTypes.string.isRequired,
  /** Plural name to display on Filter in case Humanize can't handle it */
  pluralName: PropTypes.string,
  /**
   * @param {array} activeFilters array of selected filters
   * @param {string} key filterKey
   * @public
  */
  onFiltersChange: PropTypes.func.isRequired,
  /**
   * Accepts Promise resolving an array of formatted options
   * @param {string} search
  */
  asyncSearch: PropTypes.func,
  /** Debouce wait for the asyncSearch */
  asyncSearchDebounce: PropTypes.number,
  /** Async loading text displayed while waiting for the promise */
  asyncLoadingText: PropTypes.string,
  /** Array of filter options objects */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      initial: PropTypes.bool,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
          initial: PropTypes.bool,
        }),
      ),
    }),
  ).isRequired,
  /** Maximum number of options to render in case of long options array (-1 is unlimited) */
  optionsRenderLimit: PropTypes.number,
  /** Placeholder text for when no matches are found */
  noMatchesPlaceholder: PropTypes.string,
  /** Placeholder text for search */
  searchPlaceholder: PropTypes.string,
  /** A prefix to prevent ID conflicts when multiple filters share the same option list */
  uniqIdPrefix: PropTypes.string,
  /** Immutable identifier for testing */
  dataTestId: PropTypes.string,
  /** parent types should not be selectable */
  disableParentSelect: PropTypes.bool,
  /** disable ability to remove selected items */
  disableRemoveSelected: PropTypes.bool,
  /** custom styles */
  styles: PropTypes.object,
  /** position of dropdown container relative to filter button */
  position: PropTypes.oneOf(['right', 'left']),
};

Filter.displayName = 'Filter';
export default Filter;
